<template>
    <div class="pdf-exporter">
        <slot>
            <button 
                v-if="showButton" 
                :class="buttonClass" 
                :disabled="isExporting" 
                @click="handleExport"
            >
                {{ buttonText }}
            </button>
        </slot>
    </div>
</template>

<script>
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default {
    name: 'PdfExporter',
    props: {
        // 要导出的内容区域的 ref 名称
        exportContentRef: {
            type: String,
            required: true
        },
        // 主内容区域的 ref 名称
        mainContentRef: {
            type: String,
            required: true
        },
        // 右侧区域的 ref 名称（可选）
        rightSectionRef: {
            type: String,
            default: ''
        },
        // 右侧区域中要导出的部分选择器（如 .right-score）
        rightSectionSelector: {
            type: String,
            default: '.right-score'
        },
        // ECharts 图表实例（可选，用于导出雷达图等）
        chartInstance: {
            type: Object,
            default: null
        },
        // 要排除的元素选择器数组
        excludeSelectors: {
            type: Array,
            default: () => ['.analysis-btn']
        },
        // PDF 文件名
        filename: {
            type: String,
            default: () => `面试分析报告_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`
        },
        // 导出选项
        options: {
            type: Object,
            default: () => ({
                scale: 1.2,
                backgroundColor: '#ffffff',
                radarWidthRatio: 0.7, // 雷达图宽度占主内容宽度的比例
                layout: 'vertical' // 'vertical' | 'horizontal'
            })
        },
        // 是否显示默认按钮
        showButton: {
            type: Boolean,
            default: false
        },
        // 按钮文本
        buttonText: {
            type: String,
            default: '导出 PDF'
        },
        // 按钮样式类
        buttonClass: {
            type: String,
            default: 'analysis-btn'
        }
    },
    data() {
        return {
            isExporting: false
        };
    },
    methods: {
        async handleExport() {
            await this.exportPdf();
        },
        
        async exportPdf() {
            if (this.isExporting) return;
            
            this.isExporting = true;
            this.$message && this.$message.info('正在生成 PDF，请稍候...');
            
            // 使用 npm 包（已在文件顶部导入），如果导入失败则使用 CDN 作为备用
            let html2canvasLib, jsPDFLib;
            
            // 优先使用已导入的 npm 包
            try {
                html2canvasLib = html2canvas;
                jsPDFLib = jsPDF;
                console.log('[PDF导出] 使用 npm 包的 html2canvas 和 jsPDF');
            } catch (error) {
                // 如果导入失败，尝试从 window 获取或使用 CDN
                console.warn('[PDF导出] npm 包导入失败，尝试备用方案:', error);
                
                // html2canvas 备用方案
                if (window.html2canvas) {
                    html2canvasLib = window.html2canvas;
                } else {
                    await new Promise((resolve, reject) => {
                        const script = document.createElement('script');
                        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
                        script.onload = () => {
                            html2canvasLib = window.html2canvas;
                            if (!html2canvasLib) {
                                reject(new Error('html2canvas 对象未找到'));
                            } else {
                                resolve();
                            }
                        };
                        script.onerror = () => reject(new Error('加载 html2canvas 失败'));
                        setTimeout(() => {
                            if (!window.html2canvas) {
                                reject(new Error('html2canvas 加载超时'));
                            }
                        }, 10000);
                        document.head.appendChild(script);
                    });
                }
                
                // jsPDF 备用方案
                if (window.jspdf || window.jsPDF) {
                    jsPDFLib = window.jsPDF || window.jspdf?.jsPDF || window.jspdf;
                } else {
                    await new Promise((resolve, reject) => {
                        const script = document.createElement('script');
                        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
                        script.onload = () => {
                            jsPDFLib = window.jsPDF || window.jspdf?.jsPDF || window.jspdf;
                            if (!jsPDFLib) {
                                reject(new Error('jsPDF 对象未找到'));
                            } else {
                                resolve();
                            }
                        };
                        script.onerror = () => reject(new Error('加载 jsPDF 失败'));
                        setTimeout(() => {
                            if (!window.jspdf && !window.jsPDF) {
                                reject(new Error('jsPDF 加载超时'));
                            }
                        }, 10000);
                        document.head.appendChild(script);
                    });
                }
            }
            
            if (!html2canvasLib || !jsPDFLib) {
                const errorMsg = 'PDF 导出功能加载失败: html2canvas 或 jsPDF 未找到';
                this.$message && this.$message.error(errorMsg);
                this.isExporting = false;
                return;
            }
            
            const html2canvasFn = html2canvasLib;
            const jsPDFConstructor = jsPDFLib;
            
            
            // 获取要导出的元素
            const exportContentEl = this.$parent.$refs[this.exportContentRef];
            const mainContentEl = this.$parent.$refs[this.mainContentRef];
            
            if (!exportContentEl || !mainContentEl) {
                const errorMsg = '无法找到要导出的内容区域';
                this.$message && this.$message.error(errorMsg);
                this.isExporting = false;
                return;
            }
            
            let wrapperContainer = null;
            let element = null;
            
            try {
                // 创建包装容器
                wrapperContainer = document.createElement('div');
                wrapperContainer.id = 'pdf-export-wrapper';
                // 计算总宽度：主内容宽度 + 雷达图宽度 + 间距
                const mainContentWidth = mainContentEl.offsetWidth;
                const radarWidth = Math.floor(mainContentWidth * (this.options.radarWidthRatio || 0.4));
                const totalWidth = mainContentWidth + radarWidth + 40; // 40px 是间距
                
                Object.assign(wrapperContainer.style, {
                    position: 'absolute',
                    left: '-9999px',
                    top: '0',
                    width: totalWidth + 'px',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column', // 改为纵向布局：第一行是推荐指数+雷达图，第二行是面试记录
                    gap: '20px',
                    backgroundColor: this.options.backgroundColor || '#ffffff',
                    zIndex: '-9999',
                    visibility: 'visible',
                    opacity: '1',
                    padding: '20px',
                    boxSizing: 'border-box',
                    pointerEvents: 'none',
                    overflow: 'visible',
                    border: 'none', // 确保没有边框
                    borderLeft: 'none', // 确保没有左边框
                    outline: 'none' // 确保没有轮廓
                });
                
                // 克隆主内容
                const mainClone = mainContentEl.cloneNode(true);
                Object.assign(mainClone.style, {
                    width: mainContentWidth + 'px', // 固定宽度，占左边
                    height: 'auto',
                    maxHeight: 'none',
                    overflow: 'visible',
                    flexShrink: '0',
                    marginTop: '0',
                    border: 'none', // 确保没有边框
                    borderLeft: 'none', // 确保没有左边框
                    paddingLeft: '0' // 确保没有左边距导致竖线
                });
                
                // 处理右侧区域（如果有）
                let rightSectionClone = null;
                if (this.rightSectionRef) {
                    const rightSectionEl = this.$parent.$refs[this.rightSectionRef];
                    if (rightSectionEl) {
                        const targetEl = rightSectionEl.querySelector(this.rightSectionSelector);
                        if (targetEl) {
                            rightSectionClone = targetEl.cloneNode(true);
                            
                            // 应用雷达图样式（使用之前计算的 radarWidth）
                            let originalAspectRatio = 1.2;
                            if (this.chartInstance) {
                                try {
                                    const originalChartWidth = this.chartInstance.getWidth();
                                    const originalChartHeight = this.chartInstance.getHeight();
                                    if (originalChartWidth > 0) {
                                        originalAspectRatio = originalChartHeight / originalChartWidth;
                                    }
                                } catch (e) {
                                    console.warn('[PDF导出] 无法获取原始图表尺寸，使用默认宽高比:', e);
                                }
                            }
                            
                            const radarChartHeight = Math.floor(radarWidth * originalAspectRatio);
                            const radarHeight = radarChartHeight + 60;
                            
                            Object.assign(rightSectionClone.style, {
                                width: radarWidth + 'px',
                                height: 'auto', // 改为自动高度，让内容决定
                                minHeight: radarHeight + 'px',
                                maxHeight: 'none', // 移除最大高度限制
                                overflow: 'visible',
                                flexShrink: '0',
                                marginBottom: '0', // 移除底部边距
                                boxSizing: 'border-box',
                                margin: '0',
                                alignSelf: 'flex-start' // 顶部对齐
                            });
                            
                            // 处理图表容器
                            const clonedRadarChart = rightSectionClone.querySelector('.radar-chart');
                            if (clonedRadarChart) {
                                Object.assign(clonedRadarChart.style, {
                                    width: '100%',
                                    height: radarChartHeight + 'px',
                                    minHeight: radarChartHeight + 'px',
                                    maxHeight: radarChartHeight + 'px'
                                });
                            }
                            
                            // 移除排除的元素
                            this.excludeSelectors.forEach(selector => {
                                const elements = rightSectionClone.querySelectorAll(selector);
                                elements.forEach(el => el.remove());
                            });
                        }
                    }
                }
                
                // 移除主内容中的排除元素
                this.excludeSelectors.forEach(selector => {
                    const elements = mainClone.querySelectorAll(selector);
                    elements.forEach(el => el.remove());
                });
                
                // 修改样式，让内容自适应
                const tempMainContentEl = mainClone;
                if (tempMainContentEl) {
                    Object.assign(tempMainContentEl.style, {
                        overflow: 'visible',
                        height: 'auto',
                        maxHeight: 'none'
                    });
                }
                
                // 修改优缺点部分的样式
                const tempAdvantageEl = mainClone.querySelector('.advantage');
                const tempDisadvantageEl = mainClone.querySelector('.disadvantage');
                const tempRecommendationIndexEl = mainClone.querySelector('.recommendation-index');
                const tempRecommendationContentEl = mainClone.querySelector('.recommendation-content');
                
                // 删除用户反馈按钮
                const feedbackTextEl = mainClone.querySelector('.feedback-text');
                if (feedbackTextEl) {
                    feedbackTextEl.remove();
                }
                
                // 修改"候选人推荐指数"标题样式，与雷达图标题一致
                const recommendationIndexHeader = mainClone.querySelector('.index-header h2');
                if (recommendationIndexHeader) {
                    Object.assign(recommendationIndexHeader.style, {
                        fontSize: '28px',
                        color: '#656587',
                        fontWeight: '700',
                        margin: '0',
                        padding: '0'
                    });
                }
                
                if (tempRecommendationIndexEl) {
                    Object.assign(tempRecommendationIndexEl.style, {
                        display: 'block',
                        visibility: 'visible',
                        opacity: '1',
                        height: 'auto',
                        maxHeight: 'none',
                        overflow: 'visible',
                        position: 'static', // 避免定位问题
                        zIndex: 'auto'
                    });
                }
                
                // 修改 recommendation-content 为竖着排列
                if (tempRecommendationContentEl) {
                    Object.assign(tempRecommendationContentEl.style, {
                        display: 'flex',
                        flexDirection: 'column', // 竖着排列
                        alignItems: 'stretch', // 拉伸对齐
                        gap: '20px',
                        width: '100%'
                    });
                }
                
                if (tempAdvantageEl) {
                    Object.assign(tempAdvantageEl.style, {
                        height: 'auto',
                        maxHeight: 'none',
                        overflow: 'visible',
                        display: 'block',
                        visibility: 'visible',
                        position: 'static', // 避免定位问题
                        zIndex: 'auto',
                        marginTop: '0', // 确保没有负边距
                        marginBottom: '0', // 移除底部边距，由 gap 控制
                        width: '100%', // 占满宽度
                        flex: 'none' // 不使用 flex: 1
                    });
                    
                    // 增大advantage下的字体
                    const advantageTitle = tempAdvantageEl.querySelector('.title');
                    const advantageItems = tempAdvantageEl.querySelectorAll('.item-title, .item-desc');
                    if (advantageTitle) {
                        const currentFontSize = window.getComputedStyle(advantageTitle).fontSize;
                        const newFontSize = parseFloat(currentFontSize) * 1.2 + 'px';
                        Object.assign(advantageTitle.style, {
                            fontSize: newFontSize
                        });
                    }
                    advantageItems.forEach(item => {
                        const currentFontSize = window.getComputedStyle(item).fontSize;
                        const newFontSize = parseFloat(currentFontSize) * 1.2 + 'px';
                        Object.assign(item.style, {
                            fontSize: newFontSize
                        });
                    });
                }
                if (tempDisadvantageEl) {
                    Object.assign(tempDisadvantageEl.style, {
                        height: 'auto',
                        maxHeight: 'none',
                        overflow: 'visible',
                        display: 'block',
                        visibility: 'visible',
                        position: 'static', // 避免定位问题
                        zIndex: 'auto',
                        marginTop: '0', // 确保没有负边距
                        marginBottom: '0', // 移除底部边距，由 gap 控制
                        width: '100%', // 占满宽度
                        flex: 'none' // 不使用 flex: 1
                    });
                    
                    // 增大disadvantage下的字体
                    const disadvantageTitle = tempDisadvantageEl.querySelector('.title');
                    const disadvantageItems = tempDisadvantageEl.querySelectorAll('.item-title, .item-desc');
                    if (disadvantageTitle) {
                        const currentFontSize = window.getComputedStyle(disadvantageTitle).fontSize;
                        const newFontSize = parseFloat(currentFontSize) * 1.2 + 'px';
                        Object.assign(disadvantageTitle.style, {
                            fontSize: newFontSize
                        });
                    }
                    disadvantageItems.forEach(item => {
                        const currentFontSize = window.getComputedStyle(item).fontSize;
                        const newFontSize = parseFloat(currentFontSize) * 1.2 + 'px';
                        Object.assign(item.style, {
                            fontSize: newFontSize
                        });
                    });
                }
                
                // 通用方法：修复所有元素的样式，避免遮挡和定位问题
                const allStyleElements = mainClone.querySelectorAll('*');
                allStyleElements.forEach(el => {
                    // 跳过脚本、样式等元素
                    if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'NOSCRIPT') {
                        return;
                    }
                    
                    // 获取计算后的样式
                    const computedStyle = window.getComputedStyle(el);
                    const position = computedStyle.position;
                    const height = computedStyle.height;
                    const maxHeight = computedStyle.maxHeight;
                    
                    // 修复定位问题：如果元素是相对定位或绝对定位，改为 static
                    if (position === 'relative' || position === 'absolute' || position === 'fixed') {
                        Object.assign(el.style, {
                            position: 'static',
                            zIndex: 'auto'
                        });
                    }
                    
                    // 修复溢出问题：如果元素有固定高度或最大高度限制，移除限制
                    if (height !== 'auto' || maxHeight !== 'none') {
                        Object.assign(el.style, {
                            overflow: 'visible',
                            height: 'auto',
                            maxHeight: 'none'
                        });
                    }
                    
                    // 移除左边框，避免竖线
                    if (computedStyle.borderLeft && computedStyle.borderLeft !== 'none' && computedStyle.borderLeft !== '0px') {
                        el.style.borderLeft = 'none';
                    }
                });
                
                // 移除伪元素 ::before（通过添加样式来隐藏它）
                if (!document.getElementById('pdf-export-hide-pseudo')) {
                    const style = document.createElement('style');
                    style.id = 'pdf-export-hide-pseudo';
                    style.textContent = `
                        #pdf-export-wrapper *::before {
                            display: none !important;
                            content: none !important;
                            visibility: hidden !important;
                            width: 0 !important;
                            height: 0 !important;
                        }
                    `;
                    document.head.appendChild(style);
                }
                
                // 分离面试记录
                const tempInterviewRecordEl = mainClone.querySelector('.interview-record');
                let interviewRecordClone = null;
                if (tempInterviewRecordEl) {
                    interviewRecordClone = tempInterviewRecordEl.cloneNode(true);
                    // 从主内容中移除面试记录
                    tempInterviewRecordEl.remove();
                    // 设置面试记录样式，占满整个PDF宽度
                    Object.assign(interviewRecordClone.style, {
                        width: totalWidth + 'px', // 使用总宽度，占满整个PDF
                        maxWidth: '100%',
                        marginLeft: '0',
                        marginRight: '0',
                        paddingLeft: '0',
                        paddingRight: '0',
                        overflow: 'visible',
                        height: 'auto',
                        maxHeight: 'none',
                        boxSizing: 'border-box'
                    });
                    
                    // 删除导出面试记录按钮
                    const exportBtn = interviewRecordClone.querySelector('.export-btn');
                    if (exportBtn) {
                        exportBtn.remove();
                    }
                    
                    // 修改"面试记录"标题样式，与雷达图标题一致
                    const interviewRecordHeader = interviewRecordClone.querySelector('.record-header h3');
                    if (interviewRecordHeader) {
                        Object.assign(interviewRecordHeader.style, {
                            fontSize: '28px',
                            color: '#656587',
                            fontWeight: '700',
                            margin: '0',
                            padding: '0'
                        });
                    }
                    
                    // 增大interview-record下的字体
                    const interviewRecordTextElements = interviewRecordClone.querySelectorAll('p, .interview-summary, .qa-section, .question-content, .answer-content, .interviewer-advice');
                    interviewRecordTextElements.forEach(el => {
                        const currentFontSize = window.getComputedStyle(el).fontSize;
                        if (currentFontSize && currentFontSize !== 'inherit') {
                            const newFontSize = parseFloat(currentFontSize) * 1.2 + 'px';
                            Object.assign(el.style, {
                                fontSize: newFontSize
                            });
                        }
                    });
                    
                    // 确保面试记录内部的所有元素也占满宽度
                    const interviewRecordChildren = interviewRecordClone.querySelectorAll('*');
                    interviewRecordChildren.forEach(child => {
                        const computedStyle = window.getComputedStyle(child);
                        if (computedStyle.width && computedStyle.width !== '100%' && computedStyle.width !== 'auto') {
                            // 如果子元素有固定宽度，改为100%
                            if (child.classList.contains('record-header') || child.classList.contains('interview-summary') || child.classList.contains('qa-section')) {
                                Object.assign(child.style, {
                                    width: '100%',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box'
                                });
                            }
                        }
                    });
                }
                
                // 创建第一行容器：推荐指数 + 雷达图（横向布局）
                const firstRowContainer = document.createElement('div');
                Object.assign(firstRowContainer.style, {
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '20px',
                    width: '100%',
                    alignItems: 'flex-start'
                });
                
                // 设置主内容宽度（推荐指数部分）
                Object.assign(mainClone.style, {
                    width: mainContentWidth + 'px',
                    flexShrink: '0'
                });
                
                // 添加推荐指数到第一行
                firstRowContainer.appendChild(mainClone);
                // 添加雷达图到第一行（如果有）
                if (rightSectionClone) {
                    firstRowContainer.appendChild(rightSectionClone);
                }
                
                // 创建标题元素
                const titleElement = document.createElement('div');
                titleElement.textContent = '面试分析报告';
                Object.assign(titleElement.style, {
                    width: '100%',
                    fontSize: '32px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    padding: '20px 0',
                    marginBottom: '20px',
                    color: '#333',
                    borderBottom: '2px solid #e0e0e0',
                    boxSizing: 'border-box'
                });
                
                // 添加标题到容器最前面
                wrapperContainer.appendChild(titleElement);
                
                // 添加第一行到主容器
                wrapperContainer.appendChild(firstRowContainer);
                
                // 添加面试记录到第二行（单独占一行，宽度100%）
                if (interviewRecordClone) {
                    wrapperContainer.appendChild(interviewRecordClone);
                }
                
                document.body.appendChild(wrapperContainer);
                
                // 等待 DOM 渲染
                await this.$nextTick();
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // 处理图表转换为图片
                if (rightSectionClone && this.chartInstance) {
                    const clonedRadarChartRef = rightSectionClone.querySelector('.radar-chart');
                    if (clonedRadarChartRef) {
                        try {
                            // 使用之前计算的 radarWidth，保持一致性
                            const radarChartWidth = clonedRadarChartRef.offsetWidth || radarWidth;
                            const originalChartWidth = this.chartInstance.getWidth();
                            const originalChartHeight = this.chartInstance.getHeight();
                            const originalAspectRatio = originalChartHeight / originalChartWidth;
                            const targetHeight = Math.floor(radarChartWidth * originalAspectRatio);
                            
                            // 临时调整图表大小
                            this.chartInstance.resize(radarChartWidth, targetHeight);
                            await new Promise(resolve => setTimeout(resolve, 300));
                            
                            // 获取图表图片
                            const radarChartImageUrl = this.chartInstance.getDataURL({
                                type: 'png',
                                pixelRatio: 2,
                                backgroundColor: '#ffffff'
                            });
                            
                            // 恢复原始大小
                            this.chartInstance.resize(originalChartWidth, originalChartHeight);
                            
                            // 替换为图片
                            clonedRadarChartRef.innerHTML = '';
                            const radarImage = document.createElement('img');
                            radarImage.src = radarChartImageUrl;
                            Object.assign(radarImage.style, {
                                width: '100%',
                                height: targetHeight + 'px',
                                minHeight: targetHeight + 'px',
                                maxHeight: targetHeight + 'px',
                                display: 'block',
                                objectFit: 'contain'
                            });
                            clonedRadarChartRef.appendChild(radarImage);
                            
                            // 等待图片加载
                            await new Promise((resolve, reject) => {
                                radarImage.onload = () => resolve();
                                radarImage.onerror = () => reject(new Error('雷达图图片加载失败'));
                                setTimeout(() => {
                                    if (!radarImage.complete) {
                                        reject(new Error('雷达图图片加载超时'));
                                    }
                                }, 3000);
                            });
                        } catch (error) {
                            console.error('[PDF导出] 转换雷达图为图片失败:', error);
                        }
                    }
                }
                
                element = wrapperContainer;
                
                // 等待样式应用和 DOM 完全渲染
                await this.$nextTick();
                await new Promise(resolve => setTimeout(resolve, 500)); // 增加等待时间，确保样式完全应用
                
                // 强制重新计算布局，确保所有元素的位置和尺寸都是最新的
                element.offsetHeight; // 触发重排
                await new Promise(resolve => setTimeout(resolve, 200));
                
                // 计算容器尺寸
                let containerHeight = 0;
                let containerWidth = 0;
                const children = Array.from(element.children);
                if (children.length > 0) {
                    children.forEach(child => {
                        const childHeight = Math.max(
                            child.scrollHeight,
                            child.offsetHeight,
                            child.getBoundingClientRect().height
                        );
                        const childWidth = Math.max(
                            child.scrollWidth,
                            child.offsetWidth,
                            child.getBoundingClientRect().width
                        );
                        containerHeight += childHeight;
                        containerWidth = Math.max(containerWidth, childWidth);
                    });
                    containerHeight += (children.length - 1) * 20;
                } else {
                    containerHeight = Math.max(
                        element.scrollHeight || 0,
                        element.offsetHeight || 0,
                        element.getBoundingClientRect().height || 0
                    );
                    containerWidth = Math.max(
                        element.scrollWidth || 0,
                        element.offsetWidth || 0,
                        element.getBoundingClientRect().width || 0
                    );
                }
                
                if (containerWidth <= 0) containerWidth = 1200;
                if (containerHeight <= 0) containerHeight = 1600;
                
                // 使用 html2canvas 渲染
                const canvas = await html2canvasFn(element, {
                    scale: this.options.scale || 1.2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: this.options.backgroundColor || '#ffffff',
                    allowTaint: false,
                    width: element.offsetWidth,
                    height: element.offsetHeight
                });
                
                // 创建 PDF
                const imgWidth = 210; // A4 宽度（mm）
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                
                const pdf = new jsPDFConstructor({
                    orientation: imgHeight > imgWidth ? 'portrait' : 'landscape',
                    unit: 'mm',
                    format: 'a4',
                    compress: true
                });
                
                // 分页逻辑
                const imgData = canvas.toDataURL('image/jpeg', 0.92);
                const pageHeight = pdf.internal.pageSize.getHeight();
                const pageWidth = pdf.internal.pageSize.getWidth();
                const margin = 10;
                const usablePageHeight = pageHeight - (margin * 2);
                const usablePageWidth = pageWidth - (margin * 2);
                const imgHeightInMM = (imgHeight * usablePageWidth) / imgWidth;
                
                const pixelsPerMM = canvas.height / imgHeightInMM;
                const pixelsPerPage = usablePageHeight * pixelsPerMM;
                
                // 计算坐标转换比例（DOM 坐标 -> Canvas 坐标）
                // html2canvas 使用 scale 参数，所以需要将 DOM 坐标乘以 scale
                const scale = this.options.scale || 1.2;
                const elementRect = element.getBoundingClientRect();
                
                // 通用方法：检测所有包含文本的元素，确保所有文字都不被分割
                // 更简单直接的方法：检测所有可能包含文本的元素
                const allElements = element.querySelectorAll('*');
                const textElements = [];
                const processedTextElements = new Set();
                
                allElements.forEach(el => {
                    // 跳过脚本、样式等元素
                    if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'NOSCRIPT' || el.tagName === 'IMG' || el.tagName === 'CANVAS') {
                        return;
                    }
                    
                    // 跳过雷达图容器内的所有元素（雷达图容器会作为一个整体被保护）
                    if (rightSectionClone && rightSectionClone.contains(el)) {
                        return;
                    }
                    
                    // 检查元素是否包含文本内容（更宽松的检测）
                    const text = el.textContent?.trim() || '';
                    if (!text) return;
                    
                    // 检查是否是文本节点或包含文本的块级元素
                    const isTextNode = el.children.length === 0 && text.length > 0;
                    const isBlockElement = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'DT', 'DD', 'SPAN', 'LABEL', 'BUTTON', 'A'].includes(el.tagName);
                    const hasDirectText = Array.from(el.childNodes).some(node => node.nodeType === 3 && node.textContent?.trim().length > 0);
                    
                    // 特别处理面试记录中的元素，确保所有文本都被保护
                    const isInInterviewRecord = el.closest('.interview-record') !== null;
                    
                    if (isTextNode || (isBlockElement && text.length > 0) || hasDirectText || (isInInterviewRecord && text.length > 0)) {
                        if (!processedTextElements.has(el)) {
                            textElements.push(el);
                            processedTextElements.add(el);
                        }
                    }
                });
                
                // 通用方法：为所有包含文本的元素创建保护块
                const blockPositions = [];
                const processedContainers = new Set();
                
                // 确保雷达图容器作为一个整体被保护（如果存在）
                // 这是最高优先级，确保雷达图（包括所有文字）不被分割
                // 注意：在 html2canvas 之后，需要基于 canvas 坐标计算位置
                if (rightSectionClone) {
                    // 获取雷达图容器在 DOM 中的位置
                    const radarRect = rightSectionClone.getBoundingClientRect();
                    const radarScrollHeight = rightSectionClone.scrollHeight;
                    const radarOffsetHeight = rightSectionClone.offsetHeight;
                    const radarClientHeight = rightSectionClone.clientHeight;
                    
                    // 获取所有子元素的最大底部位置
                    let maxChildBottom = 0;
                    const children = rightSectionClone.children;
                    for (let i = 0; i < children.length; i++) {
                        const childRect = children[i].getBoundingClientRect();
                        const childBottom = childRect.bottom - radarRect.top;
                        maxChildBottom = Math.max(maxChildBottom, childBottom);
                    }
                    
                    // 使用最大的高度值，确保包含所有内容
                    const actualHeight = Math.max(
                        radarRect.height,
                        radarScrollHeight,
                        radarOffsetHeight,
                        radarClientHeight,
                        maxChildBottom
                    );
                    
                    // 计算相对于 element 的位置（DOM 坐标）
                    const radarTop = radarRect.top - elementRect.top;
                    const radarBottom = radarTop + actualHeight;
                    
                    // 转换为 canvas 坐标（乘以 scale）
                    const canvasTop = radarTop * scale;
                    const canvasBottom = radarBottom * scale;
                    
                    if (actualHeight > 0 && canvasTop >= 0) {
                        // 添加一个更大的保护范围，确保雷达图及其周围的内容都不被分割
                        const padding = 50 * scale; // 在 canvas 坐标中增加缓冲区域
                        const protectedTop = Math.max(0, canvasTop - padding);
                        const protectedBottom = canvasBottom + padding;
                        
                        blockPositions.push({
                            top: protectedTop,
                            bottom: protectedBottom,
                            height: protectedBottom - protectedTop,
                            element: rightSectionClone,
                            isContainer: true,
                            className: 'radar-chart-container',
                            priority: 0 // 最高优先级
                        });
                        processedContainers.add(rightSectionClone);
                    }
                }
                
                textElements.forEach(textEl => {
                    // 跳过已经在雷达图容器内的元素（已经被保护）
                    if (rightSectionClone && rightSectionClone.contains(textEl)) {
                        return;
                    }
                    
                    // 面试记录内的元素需要单独保护，不要跳过（允许面试记录跨页，但内部文本不被分割）
                    
                    const rect = textEl.getBoundingClientRect();
                    const containerRect = element.getBoundingClientRect();
                    // DOM 坐标
                    const relativeTop = rect.top - containerRect.top;
                    const relativeBottom = rect.bottom - containerRect.top;
                    
                    // 转换为 canvas 坐标
                    const canvasTop = relativeTop * scale;
                    const canvasBottom = relativeBottom * scale;
                    
                    if (rect.height <= 0 || canvasTop < 0) return;
                    
                    // 找到包含这个文本元素的最近的有意义的父容器
                    let parentContainer = textEl.parentElement;
                    let bestContainer = null;
                    let searchDepth = 0;
                    const maxDepth = 10; // 限制搜索深度，避免无限循环
                    
                    // 向上查找，找到合适的容器（有类名或者是块级元素）
                    while (parentContainer && parentContainer !== element && searchDepth < maxDepth) {
                        // 如果父容器是雷达图容器，跳过（已经被保护）
                        if (rightSectionClone && rightSectionClone === parentContainer) {
                            return;
                        }
                        
                        // 如果父容器是面试记录容器，继续查找其内部的子容器（允许面试记录跨页）
                        if (interviewRecordClone && interviewRecordClone === parentContainer) {
                            parentContainer = parentContainer.parentElement;
                            searchDepth++;
                            continue;
                        }
                        
                        const hasClass = parentContainer.className && typeof parentContainer.className === 'string' && parentContainer.className.trim().length > 0;
                        const isBlockElement = ['DIV', 'SECTION', 'ARTICLE', 'MAIN', 'ASIDE', 'HEADER', 'FOOTER', 'UL', 'OL', 'DL', 'P'].includes(parentContainer.tagName);
                        
                        if (hasClass || isBlockElement) {
                            bestContainer = parentContainer;
                            break;
                        }
                        parentContainer = parentContainer.parentElement;
                        searchDepth++;
                    }
                    
                    // 如果找到了合适的容器，保护整个容器
                    if (bestContainer) {
                        const containerKey = bestContainer;
                        if (!processedContainers.has(containerKey)) {
                            const containerRect2 = bestContainer.getBoundingClientRect();
                            // DOM 坐标
                            const containerTop = containerRect2.top - containerRect.top;
                            const containerBottom = containerRect2.bottom - containerRect.top;
                            
                            // 转换为 canvas 坐标
                            const canvasContainerTop = containerTop * scale;
                            const canvasContainerBottom = containerBottom * scale;
                            
                            // 确保容器位置有效
                            if (canvasContainerTop >= 0 && canvasContainerBottom > canvasContainerTop) {
                                blockPositions.push({
                                    top: canvasContainerTop,
                                    bottom: canvasContainerBottom,
                                    height: canvasContainerBottom - canvasContainerTop,
                                    element: bestContainer,
                                    isContainer: true,
                                    className: bestContainer.className || bestContainer.tagName
                                });
                                processedContainers.add(containerKey);
                            }
                        }
                    } else {
                        // 如果没有找到合适的容器，直接保护文本元素本身（使用 canvas 坐标）
                        blockPositions.push({
                            top: canvasTop,
                            bottom: canvasBottom,
                            height: canvasBottom - canvasTop,
                            element: textEl,
                            isContainer: true,
                            className: textEl.className || textEl.tagName || textEl.tagName.toLowerCase()
                        });
                    }
                });
                
                blockPositions.sort((a, b) => a.top - b.top);
            
                
                // 分页 - 简化逻辑，只在必要时调整
                let currentY = 0; // 从0开始，确保第一页正常显示
                let pageNum = 0;
                
                if (imgHeightInMM <= usablePageHeight) {
                    // 如果内容高度小于一页，直接添加（确保第一页正常显示）
                    if (canvas.height > 0) {
                        pdf.addImage(imgData, 'JPEG', margin, margin, usablePageWidth, imgHeightInMM);
                    }
                } else {
                    while (currentY < canvas.height) {
                        let nextPageY = currentY + pixelsPerPage;
                        
                        // 通用方法：检查所有保护块，确保文字不被分割
                        // 优先处理雷达图容器（最高优先级），然后处理其他块
                        let minAdjustedY = nextPageY;
                        let foundAdjustment = false;
                        
                        // 先检查雷达图容器（如果有，最高优先级）
                        const radarBlock = blockPositions.find(b => b.className === 'radar-chart-container');
                        if (radarBlock) {
                            // 只处理在当前页范围内的块
                            if (radarBlock.top >= currentY && radarBlock.bottom >= currentY) {
                                // 如果分页点在雷达图容器内部，从容器顶部开始新页
                                if (nextPageY > radarBlock.top && nextPageY < radarBlock.bottom) {
                                    // 第一页时，允许雷达图跨页显示，不要强制移到新页
                                    if (pageNum === 0) {
                                        // 允许第一页的内容跨页显示
                                    } else {
                                        minAdjustedY = Math.min(minAdjustedY, radarBlock.top);
                                        foundAdjustment = true;
                                    }
                                }
                                
                                // 如果雷达图容器在当前页开始，但底部超出当前页，从容器顶部开始新页
                                if (radarBlock.top >= currentY && radarBlock.top < nextPageY && radarBlock.bottom > nextPageY) {
                                    // 第一页时，允许雷达图跨页显示，不要强制移到新页
                                    if (pageNum === 0) {
                                        // 允许第一页的内容跨页显示
                                    } else {
                                        minAdjustedY = Math.min(minAdjustedY, radarBlock.top);
                                        foundAdjustment = true;
                                    }
                                }
                            }
                        }
                        
                        // 然后检查其他保护块
                        for (let i = 0; i < blockPositions.length; i++) {
                            const block = blockPositions[i];
                            
                            // 跳过雷达图容器（已经处理过了）
                            if (block.className === 'radar-chart-container') {
                                continue;
                            }
                            
                            // 只处理在当前页范围内的块
                            if (block.top < currentY || block.bottom < currentY) {
                                continue;
                            }
                            
                            // 如果分页点在保护块内部（不在边界），从块顶部开始新页
                            if (nextPageY > block.top && nextPageY < block.bottom) {
                                // 第一页时，允许内容跨页显示，不要强制移到新页
                                if (pageNum === 0) {
                                    continue;
                                }
                                minAdjustedY = Math.min(minAdjustedY, block.top);
                                foundAdjustment = true;
                            }
                            
                            // 如果保护块在当前页开始，但底部超出当前页，从块顶部开始新页
                            // 但对于第一页，允许内容跨页显示，不要强制移到新页
                            if (block.top >= currentY && block.top < nextPageY && block.bottom > nextPageY) {
                                // 第一页时，允许内容跨页，不要强制移到新页
                                if (pageNum === 0) {
                                    continue;
                                }
                                minAdjustedY = Math.min(minAdjustedY, block.top);
                                foundAdjustment = true;
                            }
                        }
                        
                        // 如果找到了需要调整的块，使用调整后的位置
                        if (foundAdjustment) {
                            nextPageY = minAdjustedY;
                        }
                        
                        // 确保 nextPageY 不超过 canvas 高度
                        if (nextPageY > canvas.height) {
                            nextPageY = canvas.height;
                        }
                        
                        // 确保 nextPageY 大于 currentY（避免无限循环）
                        // 如果调整后导致 nextPageY <= currentY，说明所有块都在当前页，使用默认分页
                        if (nextPageY <= currentY) {
                            nextPageY = currentY + pixelsPerPage;
                            if (nextPageY > canvas.height) {
                                nextPageY = canvas.height;
                            }
                        }
                        
                        // 确保 pageHeightPx 是有效的，且不会导致重叠
                        const pageHeightPx = Math.min(nextPageY - currentY, canvas.height - currentY);
                        
                        // 确保页面高度大于0，避免无效页面
                        if (pageHeightPx <= 0) {
                            console.warn('[PDF导出] 页面高度无效，跳过此页');
                            break;
                        }
                        
                        // 第一页正常显示，不要跳过（让面试记录接着第一页展示）
                        // 只有在页面高度为0时才跳过，其他情况都正常显示
                        
                        if (pageNum > 0) {
                            pdf.addPage();
                        }
                        
                        const sourceY = currentY;
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = canvas.width;
                        tempCanvas.height = pageHeightPx;
                        const tempCtx = tempCanvas.getContext('2d');
                        
                        // 确保绘制时不会超出 canvas 边界，避免重叠和遮挡
                        const actualSourceHeight = Math.min(pageHeightPx, canvas.height - sourceY);
                        if (actualSourceHeight > 0) {
                            tempCtx.drawImage(canvas, 0, sourceY, canvas.width, actualSourceHeight, 0, 0, canvas.width, actualSourceHeight);
                            const pageImgData = tempCanvas.toDataURL('image/jpeg', 0.92);
                            
                            // 计算实际页面高度（毫米）
                            const actualPageHeightMM = (actualSourceHeight / pixelsPerMM);
                            pdf.addImage(pageImgData, 'JPEG', margin, margin, usablePageWidth, actualPageHeightMM);
                        }
                        
                        currentY = nextPageY;
                        pageNum++;
                        
                        // 如果剩余内容不足一页，直接添加
                        if (canvas.height - currentY <= pixelsPerPage) {
                            const remainingHeightPx = canvas.height - currentY;
                            if (remainingHeightPx > 0) {
                                const remainingHeightMM = remainingHeightPx / pixelsPerMM;
                                const remainingCanvas = document.createElement('canvas');
                                remainingCanvas.width = canvas.width;
                                remainingCanvas.height = remainingHeightPx;
                                const remainingCtx = remainingCanvas.getContext('2d');
                                remainingCtx.drawImage(canvas, 0, currentY, canvas.width, remainingHeightPx, 0, 0, canvas.width, remainingHeightPx);
                                const remainingImgData = remainingCanvas.toDataURL('image/jpeg', 0.92);
                                
                                pdf.addPage();
                                pdf.addImage(remainingImgData, 'JPEG', margin, margin, usablePageWidth, remainingHeightMM);
                            }
                            break;
                        }
                    }
                }
                
                // 保存 PDF
                pdf.save(this.filename);
                
                this.$message && this.$message.success('PDF 导出成功！');
                this.$emit('exported', this.filename);
            } catch (error) {
                console.error('PDF 导出失败:', error);
                const errorMessage = error.message || 'PDF 导出失败';
                this.$message && this.$message.error(`PDF 导出失败: ${errorMessage}`);
                this.$emit('error', error);
            } finally {
                // 清理包装容器
                if (wrapperContainer && wrapperContainer.parentNode) {
                    wrapperContainer.parentNode.removeChild(wrapperContainer);
                }
                
                // 清理临时样式
                const tempStyle = document.getElementById('pdf-export-hide-pseudo');
                if (tempStyle && tempStyle.parentNode) {
                    tempStyle.parentNode.removeChild(tempStyle);
                }
                
                this.isExporting = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.pdf-exporter {
    display: inline-block;
    width: 100%;
}

// 按钮样式 - 直接应用到组件内的按钮
.pdf-exporter button {
    width: 100%;
    background: linear-gradient(90deg, #7EB4EB 0%, #B16EF9 100%);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.pdf-exporter button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>

